import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

/** KB Settings → who gets the daily red/yellow feedback digest. */
export function KbFeedbackDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [recip, setRecip] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () =>
    api.get("/kb/feedback-digest").then(({ data }) => {
      setCfg(data);
      setRecip((data.recipients || []).join(", "));
    }).catch(() => toast.error("Couldn't load the feedback digest settings"));

  useEffect(() => { load(); }, []);

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put("/kb/feedback-digest", { recipients: recip, ...patch });
      setCfg((c) => ({ ...c, ...data }));
      setRecip((data.recipients || []).join(", "));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
      load();
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/kb/feedback-digest/send-now");
      toast.success(data.sent ? `Sent to ${(data.recipients || []).join(", ")}` : data.reason);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send it");
    } finally { setSending(false); }
  };

  if (!cfg) return null;
  const canEdit = cfg.can_edit;

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="kb-feedback-digest">
      <div className="flex flex-wrap items-center gap-3">
        <Mail className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-base font-semibold text-slate-800">Feedback digest email</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
          08:00 Mountain Time
        </span>
        <span className="ml-auto flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={sendNow} disabled={sending || !canEdit}
            data-testid="kb-feedback-digest-send-now">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
          </Button>
          <Switch checked={cfg.enabled !== false} disabled={!canEdit}
            onCheckedChange={(v) => save({ enabled: v })}
            data-testid="kb-feedback-digest-toggle" />
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-500">
        Every morning, anyone listed here gets the new red and yellow article ratings with a link
        straight to each article. Nothing is sent on a morning with no new feedback.
      </p>
      <label className="mt-3 block text-xs text-slate-600">
        Who gets this email
        <Textarea value={recip} onChange={(e) => setRecip(e.target.value)} rows={2} disabled={!canEdit}
          placeholder="virginia@unitedcloud.ca, keith@unitedcloud.ca" className="mt-1 text-sm"
          data-testid="kb-feedback-digest-recipients" />
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={() => save()} disabled={saving || !canEdit}
          data-testid="kb-feedback-digest-save">
          {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save recipients
        </Button>
        <span className="text-[13px] text-slate-500" data-testid="kb-feedback-digest-status">
          {cfg.pending
            ? `${cfg.pending} rating${cfg.pending === 1 ? "" : "s"} waiting to be reported`
            : "Nothing waiting to be reported"}
          {cfg.last_sent_at && <> · last sent {new Date(cfg.last_sent_at).toLocaleString()} to {(cfg.last_sent_to || []).join(", ")}</>}
        </span>
      </div>
    </section>
  );
}
